import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Lock, Eye, Play, Crown } from "lucide-react";
import { api, resolveUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATS = ["All", "Music Videos", "Live Performances", "Behind the Scenes", "Devotionals", "Artist Development"];

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/videos").then((r) => {
      setVideos(r.data);
      const featured = r.data.find((v) => v.is_featured) || r.data[0];
      setActive(featured);
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => (cat === "All" ? videos : videos.filter((v) => v.category === cat)), [videos, cat]);
  const isMember = user?.membership_tier || user?.is_admin;

  return (
    <div>
      {active && (
        <section className="bg-[#0B1526] text-white" data-testid="video-player-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2">
                {active.is_premium && !isMember ? (
                  <div className="aspect-video relative bg-gradient-to-br from-[#1F3864] to-[#0B1526] flex flex-col items-center justify-center text-center border border-white/10">
                    <Lock className="w-10 h-10 text-[#C00000] mb-4" />
                    <h3 className="font-display text-3xl mb-2">Members Only</h3>
                    <p className="text-white/70 max-w-md mb-6">This video is part of our exclusive members collection. Join to unlock instantly.</p>
                    <Link to="/membership" className="btn-primary-red" data-testid="action-join-from-video-button">
                      <Crown className="w-4 h-4" /> Become A Member
                    </Link>
                  </div>
                ) : (
                  <div className="aspect-video video-embed bg-black">
                    <iframe
                      title={active.title}
                      src={`https://www.youtube.com/embed/${active.youtube_id}?autoplay=1&rel=0`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
                <div className="mt-6">
                  <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-2">{active.category}</div>
                  <h1 className="font-display text-3xl sm:text-4xl mb-3">{active.title}</h1>
                  <div className="flex items-center gap-4 text-sm text-white/60">
                    <span>{active.artist_name}</span>
                    <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{(active.views || 0).toLocaleString()}</span>
                    {active.is_premium && <span className="inline-flex items-center gap-1 text-[#C00000]"><Crown className="w-3.5 h-3.5" />Premium</span>}
                  </div>
                </div>
              </div>
              <aside className="space-y-3 max-h-[600px] overflow-y-auto">
                <div className="text-xs tracking-[0.3em] uppercase text-white/50 mb-2">Up Next</div>
                {videos.slice(0, 8).filter((v) => v.id !== active.id).map((v) => (
                  <button key={v.id} onClick={() => setActive(v)} className="w-full flex gap-3 text-left hover:bg-white/5 p-2 -mx-2 transition-colors" data-testid={`video-queue-${v.id}`}>
                    <div className="w-28 aspect-video bg-black overflow-hidden flex-shrink-0 relative">
                      <img src={resolveUrl(v.thumbnail)} alt={v.title} className="w-full h-full object-cover" />
                      {v.is_premium && <Lock className="w-3 h-3 absolute top-1 right-1 text-[#C00000]" />}
                    </div>
                    <div>
                      <div className="text-sm font-medium leading-snug line-clamp-2">{v.title}</div>
                      <div className="text-xs text-white/50 mt-1">{v.artist_name}</div>
                    </div>
                  </button>
                ))}
              </aside>
            </div>
          </div>
        </section>
      )}

      <section className="py-12 border-b border-gray-200 sticky top-20 bg-white z-20" data-testid="videos-filters">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 flex-wrap">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${
                cat === c ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 text-gray-700 hover:border-[#1F3864]"
              }`}
              data-testid={`filter-cat-${c.replace(/\s/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="videos-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-[#1F3864] mb-8">The Library</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {filtered.map((v) => (
              <button key={v.id} onClick={() => { setActive(v); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="group text-left" data-testid={`video-card-${v.id}`}>
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  <img src={resolveUrl(v.thumbnail)} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Play className="w-14 h-14 text-white" fill="white" />
                  </div>
                  {v.is_premium && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#C00000] text-white text-[10px] tracking-[0.2em] uppercase flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Members
                    </div>
                  )}
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/70 text-white text-xs flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {(v.views || 0).toLocaleString()}
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C00000] mb-1">{v.category}</div>
                  <h3 className="font-display text-lg text-[#1F3864] leading-snug group-hover:text-[#C00000] line-clamp-2">{v.title}</h3>
                  <div className="text-xs text-gray-500 mt-1">{v.artist_name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
